import { getSharedDCCTVolumeInfo } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { reportsModel } from '@/entities/reports';

import {
  isDCCTVolumeUsable,
  MPRModel,
  useDCCTVolumeSizeHint,
} from '@/features/MPR';

import type { FullHeadMprSlicesContextValue } from '../config';
import {
  isFullHeadMprSlicesUnavailable,
  resolveFullHeadMprSlicesBranch,
  resolveToothSliceThicknessOptionsMm,
} from '../lib';

import { useFullHeadMprSliceGroups } from './useFullHeadMprSliceGroups';
import { useFullHeadMprSliceSelection } from './useFullHeadMprSliceSelection';
import { useToothBasisSeries } from './useToothBasisSeries';
import { useToothBasisSeriesPrefetch } from './useToothBasisSeriesPrefetch';
import { useToothBasisSeriesPriority } from './useToothBasisSeriesPriority';
import { useToothSliceSettings } from './useToothSliceSettings';
import { useToothSliceSourceState } from './useToothSliceSource';

export const useFullHeadMprSlicesRoot = ({
  patientID,
  reportID,
  toothID,
}: {
  patientID: string;
  reportID: string;
  toothID: string;
}): FullHeadMprSlicesContextValue => {
  const dispatch = useAppDispatch();

  const volumeStatus = useAppSelector(
    MPRModel.selectors.selectDCCTVolumeStatus({ patientID, reportID }),
  );

  useDCCTVolumeSizeHint({ patientID, reportID });

  const isEnabled = isDCCTVolumeUsable(volumeStatus);
  const fullHeadMprLoading = useAppSelector(
    reportsModel.selectors.selectFullHeadMprLoading(reportID),
  );

  useToothBasisSeriesPriority(toothID, true);

  const sourceState = useToothSliceSourceState(toothID);
  const input = sourceState.source;
  const toothInput = input?.kind === 'tooth' ? input : null;

  useToothBasisSeriesPrefetch({ input: toothInput, isEnabled });

  const thicknessOptionsMm = resolveToothSliceThicknessOptionsMm(
    toothInput ? getSharedDCCTVolumeInfo(toothInput.volumeFileID) : null,
  );

  const { canChangeOptions, recordsByTileID, sliceOptions, setSeriesOptions } =
    useToothSliceSettings({ reportID, thicknessOptionsMm, toothID });

  const {
    frames,
    loading,
    massCentroidMm,
    pendingSeries,
    profiles,
    series,
    slabThicknessMm,
    sliceStepMm,
  } = useToothBasisSeries({ input: toothInput, sliceOptions });

  const isSelectionReady =
    fullHeadMprLoading === 'succeeded' &&
    sourceState.status === 'succeeded' &&
    (!toothInput || loading === 'succeeded');

  const seriesGroups = useFullHeadMprSliceGroups({
    frames,
    pendingSeries,
    series,
    slabThicknessMm,
    sliceOptions,
    sliceStepMm,
    toothID,
  });

  const {
    fullHeadMprSliceGroups,
    hasSavedViews,
    savedViewsLoading,
    tiles,
    clearSelection,
    isClearingSelection,
    isTileSelected,
    pendingTileIDs,
    resolveViewSelection,
    selectedTiles,
    setZoomedTileID,
    toggleTile,
    zoomedTile,
    zoomedTileID,
  } = useFullHeadMprSliceSelection({
    fullHeadMprSliceGroups: seriesGroups,
    input: toothInput,
    isSelectionReady,
    isEnabled,
    onClearSelection: async () => {
      await dispatch(
        reportsModel.thunks.removeAllToothDisplaySlices({ ToothID: toothID }),
      );
    },
    massCentroidMm,
    profiles,
    recordsByTileID,
    sliceOptions,
    toothID,
  });

  const branch = resolveFullHeadMprSlicesBranch({
    groupCount: fullHeadMprSliceGroups.length + (hasSavedViews ? 1 : 0),
    hasSavedViews,
    hasSeries: !!series,
    hasToothInput: !!toothInput,
    isEnabled,
    isUnavailable:
      !hasSavedViews &&
      isFullHeadMprSlicesUnavailable({
        input,
        isVolumeReady: volumeStatus === 'ready',
      }),
    loading,
    savedViewsLoading,
  });

  return {
    tiles,
    branch,
    canChangeOptions,
    fullHeadMprSliceGroups,
    loading,
    isClearingSelection,
    isSelectionReady: isSelectionReady && savedViewsLoading === 'succeeded',
    pendingTileIDs,
    recordsByTileID,
    selectedTiles,
    sliceOptions,
    thicknessOptionsMm,
    toothInput,
    zoomedTile,
    zoomedTileID,
    clearSelection,
    isTileSelected,
    resolveViewSelection,
    setSeriesOptions,
    setZoomedTileID,
    toggleTile,
  };
};
